import { useState } from "react";
import { gql } from "@apollo/client";
import { client } from "../../../../App";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import BackArrow from "../../../../components/common/back-arrow";
import FadeInWrapper from "../../../../components/common/fade-in-wrapper";
import { notifyError } from "../../../../utils/toast";
import { getErrorMessage } from "../../../../utils/error";

export const GET_SELLER_STORE_STATE = gql`
  query GetSellerStoreState($userId: String!, $password: String!) {
    getSellerStoreState(userId: $userId, password: $password) {
      ok
      error
      state
    }
  }
`;

export const SELLER_REQUEST_LOGIN = gql`
  query sellerRequestLogin($userId: String!, $password: String!) {
    sellerRequestLogin(userId: $userId, password: $password) {
      ok
      error
      token
    }
  }
`;

const SignupCheckStatus = () => {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isChecking, setIsChecking] = useState(false);
  const navigate = useNavigate();

  const handleCheck = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!userId || !password) {
      setErrorMessage("연락처와 비밀번호를 모두 입력해주세요.");
      return;
    }

    setIsChecking(true);
    setErrorMessage("");

    try {
      const { data } = await client.query({
        query: GET_SELLER_STORE_STATE,
        fetchPolicy: "network-only",
        variables: {
          userId,
          password,
        },
      });

      if (data?.getSellerStoreState.ok) {
        setStatus(data.getSellerStoreState.state);

        if (data.getSellerStoreState.state === "요청") {
          const confirm = window.confirm(
            "입점신청이 완료되지 않았습니다. 다시 작성하시겠습니까?"
          );

          if (confirm) {
            const response = await client.query({
              query: SELLER_REQUEST_LOGIN,
              variables: { userId, password },
            });

            const { token, error } = response.data.sellerRequestLogin;

            if (error) {
              notifyError(error);
            } else {
              localStorage.setItem("sellerToken", token);
              navigate("/boss/signup/store");
            }
          }
        }
      } else {
        setStatus(null);
        setErrorMessage(
          data?.getSellerStoreState.error || "입점신청 내역을 찾을 수 없습니다."
        );
      }
    } catch (error) {
      setStatus(null);
      setErrorMessage("조회 중 오류가 발생했습니다.");
      notifyError(getErrorMessage(error));
      console.error(error);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <FadeInWrapper className="w-full max-w-md bg-white p-6 pb-10">
        <div className="mb-4" onClick={() => navigate(-1)}>
          <BackArrow />
        </div>

        <div className="mb-6 text-center">
          <h2 className="text-2xl font-semibold text-black mb-2">
            입점신청 현황 조회
          </h2>
          <p className="text-sm text-[#757575]">
            연락처와 비밀번호로 입점신청 현황을 확인해보세요.
          </p>
        </div>

        <div className="bg-gray-100 p-4 rounded-xl break-keep text-sm text-gray-700 mb-6">
          입점신청 과정이 완료되지 않았을 경우 다시 이어서 시작할 수 있습니다.
        </div>

        <form onSubmit={handleCheck} className="space-y-4">
          <div>
            <label
              htmlFor="userId"
              className="block text-sm font-medium text-black mb-2 pl-2"
            >
              연락처
            </label>
            <input
              type="tel"
              id="userId"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={11}
              value={userId}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                if (onlyNums.length <= 11) {
                  setUserId(onlyNums);
                  if (errorMessage) setErrorMessage("");
                }
              }}
              className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary text-sm text-gray-700"
              placeholder="연락처를 입력해주세요"
              required
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-black mb-2 pl-2"
            >
              비밀번호
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errorMessage) setErrorMessage("");
              }}
              className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary text-sm text-gray-700"
              placeholder="비밀번호를 입력해주세요"
              required
            />
          </div>

          {status && (
            <div
              className={clsx("rounded-xl p-4 text-center text-sm font-medium", {
                "bg-blue-50 text-blue-700": status === "요청",
                "bg-green-50 text-green-700": status === "승인",
                "bg-gray-50 text-gray-700": !["요청", "승인"].includes(status),
              })}
            >
              {status === "요청" || status === "요청완료" ? (
                <>현재 입점신청이 검토 중입니다. 조금만 기다려주세요!</>
              ) : status === "승인" ? (
                <>축하드려요! 입점이 승인되었습니다.</>
              ) : (
                "입점신청 내역을 찾을 수 없습니다"
              )}
            </div>
          )}

          {errorMessage && (
            <p className="text-red-500 text-sm mt-1 pl-2">{errorMessage}</p>
          )}

          <button
            type="submit"
            className={`w-full py-3 rounded-xl text-white font-bold ${
              isChecking
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-primary hover:brightness-90 transition duration-150"
            }`}
            disabled={isChecking}
          >
            {isChecking ? "조회 중..." : "신청 현황 확인하기"}
          </button>
        </form>
      </FadeInWrapper>
    </div>
  );
};

export default SignupCheckStatus;
